import React from 'react';
import { useTranslation } from 'react-i18next';
import { HardMediumSoftScore, convertHardMediumSoftScoreToString } from 'domain/HardMediumSoftScore';
import { Chip, Button, ButtonVariant, Popover, List } from '@patternfly/react-core';
import { IndictmentSummary } from 'domain/indictment/IndictmentSummary';
import {
  HelpIcon, ExclamationTriangleIcon, UserFriendsIcon, FileContractIcon, UserPlusIcon, PhoneVolumeIcon,
  CalendarTimesIcon, AlignCenterIcon, SchoolIcon, UserTimesIcon, UserMinusIcon,
} from '@patternfly/react-icons';

export interface ScoreDisplayProps {
  score: HardMediumSoftScore;
  indictmentSummary: IndictmentSummary;
  isSolving: boolean;
}

export function IndictmentIcon({ indictment }: { indictment: string }) {
  switch (indictment) {
    case 'Asignar cada turno':
      return (<ExclamationTriangleIcon />);

    case 'No más de 2 turnos consecutivos':
      return (<AlignCenterIcon />);
    case 'Sin turnos superpuestos':
      return (<UserFriendsIcon />);
    case 'Habilidad requerida para un turno':
      return (<SchoolIcon />);
    case 'El descanso entre turnos no consecutivos es de al menos 30 minutos':
      return (<UserFriendsIcon />);

    case 'Los minutos diarios no deben exceder el máximo del contrato':
      return (<FileContractIcon />);
    case 'Los minutos semanales no deben exceder el máximo del contrato':
      return (<FileContractIcon />);
    case 'Los minutos mensuales no deben exceder el máximo del contrato':
      return (<FileContractIcon />);
    case 'Los minutos anuales no deben exceder el máximo del contrato':
      return (<FileContractIcon />);

    case 'Franja horaria no disponible para un empleado':
      return (<UserTimesIcon />);
    case 'Franja horaria no deseada para un empleado':
      return (<UserMinusIcon />);
    case 'Franja horaria deseada para un empleado':
      return (<UserPlusIcon />);

    case 'El empleado no es el empleado original':
      return (<PhoneVolumeIcon />);
    case 'El empleado no es un empleado de rotación':
      return (<CalendarTimesIcon />);
    case 'Equilibrar las asignaciones de turnos de los empleados':
      return (<CalendarTimesIcon />);
    case 'El empleado no descansa al menos un día por semana':
      return (<CalendarTimesIcon />);
    case 'Diferentes empleados para turnos del mismo día':
      return (<CalendarTimesIcon />);

    default:
      return (<span />);
  }
}

const CONSTRAINTS = ['Asignar cada turno',
  'No más de 2 turnos consecutivos',
  'Sin turnos superpuestos',
  'Habilidad requerida para un turno',
  'El descanso entre turnos no consecutivos es de al menos 30 minutos',
  'Los minutos diarios no deben exceder el máximo del contrato',
  'Los minutos semanales no deben exceder el máximo del contrato',
  'Los minutos mensuales no deben exceder el máximo del contrato',
  'Los minutos anuales no deben exceder el máximo del contrato',
  'Franja horaria no disponible para un empleado',
  'Franja horaria no deseada para un empleado',
  'Franja horaria deseada para un empleado',
  'El empleado no es el empleado original',
  'El empleado no es un empleado de rotación',
  'Equilibrar las asignaciones de turnos de los empleados',
  'El empleado no descansa al menos un día por semana',
  'Diferentes empleados para turnos del mismo día',
];

export function ConstraintMatches({ indictmentSummary }: { indictmentSummary: IndictmentSummary }) {
  return (
    <List>
      {Object.keys(indictmentSummary.constraintToCountMap)
        .sort((a, b) => CONSTRAINTS.indexOf(b) - CONSTRAINTS.indexOf(a))
        .map((constraint) => (
          <li key={constraint}>
            <IndictmentIcon indictment={constraint} />
            <span style={{ paddingLeft: '8px' }}>
              {`${constraint}: ${indictmentSummary.constraintToCountMap[constraint]}
              (Impacto: 
              ${convertHardMediumSoftScoreToString(indictmentSummary.constraintToScoreImpactMap[constraint])
          })`}
            </span>
          </li>
        ))}
    </List>
  );
}

export function ScoreDisplay(props: ScoreDisplayProps) {
  const { t } = useTranslation('ScoreDisplay');
  const { hardScore, mediumScore, softScore } = props.score;
  const { isSolving } = props;
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <span>
      <Chip isReadOnly>
        {t('hardScore', { hardScore })}
      </Chip>
      <Chip isReadOnly>
        {t('mediumScore', { mediumScore })}
      </Chip>
      <Chip isReadOnly>
        {t('softScore', { softScore })}
      </Chip>
      <Popover
        headerContent="Evaluación de restricciones"
        bodyContent={<ConstraintMatches indictmentSummary={props.indictmentSummary} />}
        boundary="viewport"
        maxWidth="800px"
        shouldClose={() => setIsOpen(false)}
        isVisible={isOpen}
      >
        <Button variant={ButtonVariant.plain} onClick={() => setIsOpen(!isOpen)}>
          <HelpIcon />
        </Button>
      </Popover>
      {(hardScore < 0 && !isSolving) && 'El horario actualmente no es factible.'}
      {(hardScore < 0 && isSolving) && 'En espera mientras se resuelven las violaciones de restricciones estrictas...'}
      {(hardScore === 0 && mediumScore < 0 && !isSolving) && 'Algunos turnos no están asignados.'}
      {(hardScore === 0 && mediumScore < 0 && isSolving) && 'Espere mientras se asignan los turnos...'}
    </span>
  );
}
